// components/Usage.js
import React, { useState } from 'react';
import './Usage.css';
import CodeBlock from './CodeBlock';

const Usage = () => {
  const [activeTab, setActiveTab] = useState('pytorch');
  
  const pytorchCode = `# PyTorch example - most complete implementation
import torch
import torch.nn as nn
from torch.utils.data import DataLoader
from cinder import ModelDebugger

CINDER_API_KEY = "your-api-key-here"

# Define model
class SimpleModel(nn.Module):
    def __init__(self):
        super(SimpleModel, self).__init__()
        self.layer1 = nn.Linear(10, 64)
        self.layer2 = nn.Linear(64, 2)
        
    def forward(self, x):
        x = torch.relu(self.layer1(x))
        x = self.layer2(x)
        return x

# Initialize model and data
model = SimpleModel()
test_loader = DataLoader(test_dataset, batch_size=32)

# Initialize Cinder debugger
debugger = ModelDebugger(model, test_loader, api_key=CINDER_API_KEY)

# Analyze model performance
results = debugger.analyze()
print(f"Accuracy: {results['accuracy']:.4f}")

# Launch interactive dashboard with Bit assistant
debugger.launch_dashboard(port=8000)`;

  const tensorflowCode = `# TensorFlow example - beta support
import tensorflow as tf
from cinder import ModelDebugger

CINDER_API_KEY = "your-api-key"

# Create dataset wrapper
# Note: This is a simplified example
class TFDataset:
    def __init__(self, x, y, batch_size=32):
        self.x = x
        self.y = y
        self.batch_size = batch_size
        
    def __iter__(self):
        self.current = 0
        return self
        
    def __next__(self):
        if self.current >= len(self.x):
            raise StopIteration
        batch_x = self.x[self.current:self.current+self.batch_size]
        batch_y = self.y[self.current:self.current+self.batch_size]
        self.current += self.batch_size
        return batch_x, batch_y

# Define model
model = tf.keras.Sequential([
    tf.keras.layers.Dense(64, activation='relu', input_shape=(10,)),
    tf.keras.layers.Dense(1, activation='sigmoid')
])

# Create dataset and initialize debugger
test_dataset = TFDataset(x_test, y_test)
debugger = ModelDebugger(model, test_dataset, api_key=CINDER_API_KEY)

# Get basic analysis results
analysis = debugger.analyze()

# Launch dashboard (limited functionality)
debugger.launch_dashboard(port=8000)`;

  const sklearnCode = `# scikit-learn example - beta support
from sklearn.ensemble import RandomForestClassifier
from sklearn.datasets import load_iris
from sklearn.model_selection import train_test_split
from cinder import ModelDebugger

CINDER_API_KEY = "your-api-key"

# Load and prepare data
iris = load_iris()
X, y = iris.data, iris.target
X_train, X_test, y_train, y_test = train_test_split(X, y, test_size=0.3)

# Train model
model = RandomForestClassifier(n_estimators=100, random_state=42)
model.fit(X_train, y_train)

# Create dataset wrapper
# Note: This is a simplified example
class SKDataset:
    def __init__(self, X, y):
        self.X, self.y = X, y
        
    def __iter__(self):
        self.current = 0
        return self
        
    def __next__(self):
        if self.current >= len(self.X):
            raise StopIteration
        X_batch = self.X[self.current:self.current+32]
        y_batch = self.y[self.current:self.current+32]
        self.current += 32
        return X_batch, y_batch

# Initialize debugger
test_dataset = SKDataset(X_test, y_test)
debugger = ModelDebugger(
    model=model, 
    dataset=test_dataset,
    feature_names=iris.feature_names,
    api_key=CINDER_API_KEY
)

# Get basic analysis results
results = debugger.analyze()

# Launch dashboard (limited functionality)
debugger.launch_dashboard(port=8000)`;

  return (
    <section className="usage" id="usage">
      <div className="section-header">
        <h2>Implementation</h2>
        <p>Integrate Cinder into your ML workflow (PyTorch support most complete)</p>
      </div>
      
      <div className="usage-tabs">
        <button 
          className={`usage-tab ${activeTab === 'pytorch' ? 'active' : ''}`}
          onClick={() => setActiveTab('pytorch')}
        >
          PyTorch
        </button>
        <button 
          className={`usage-tab ${activeTab === 'tensorflow' ? 'active' : ''}`}
          onClick={() => setActiveTab('tensorflow')}
        >
          TensorFlow <span className="small-tag">Beta</span>
        </button>
        <button 
          className={`usage-tab ${activeTab === 'sklearn' ? 'active' : ''}`}
          onClick={() => setActiveTab('sklearn')}
        >
          scikit-learn <span className="small-tag">Beta</span>
        </button>
      </div>
      
      <div className="usage-content">
        <div className="usage-steps">
          <p className="usage-description">
            Cinder provides a consistent API across frameworks, with most complete implementation for PyTorch.
            Basic steps: 1) Install the package, 2) Create a dataset wrapper, 3) Initialize the debugger,
            4) Launch the dashboard to access Bit and other analysis tools.
          </p>
          
          <div className="install-command">
            <code>pip install "cinder-ml[{activeTab === 'pytorch' ? 'pytorch' : activeTab === 'tensorflow' ? 'tensorflow' : 'sklearn'}]"</code>
          </div>
        </div>
        
        <div className="usage-code">
          <CodeBlock 
            code={activeTab === 'pytorch' ? pytorchCode : activeTab === 'tensorflow' ? tensorflowCode : sklearnCode} 
            language="python" 
          />
        </div>
      </div>
      
      <div className="key-features">
        <div className="feature-list">
          <div className="feature-item">
            <h4>Dashboard Interface</h4>
            <p>Interactive web dashboard with visualization tools</p>
          </div>
          <div className="feature-item">
            <h4>Bit AI Assistant</h4>
            <p>AI-powered chat interface for model optimization</p>
          </div>
          <div className="feature-item">
            <h4>Error Analysis</h4>
            <p>Identify and categorize prediction errors</p>
          </div>
          <div className="feature-item">
            <h4>Framework Support</h4>
            <p>Primary PyTorch support with growing TensorFlow and scikit-learn compatibility</p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Usage;